import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Target, Star, Shield, Heart, CheckCircle, Mail, Sparkles, BookOpen, Video, FileText } from 'lucide-react';

const AssessmentResultsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailCaptured, setEmailCaptured] = useState(false);

  const sessionId = searchParams.get('session');
  const recommendedTrack = searchParams.get('track');

  useEffect(() => {
    // Show email modal after 5 seconds
    const timer = setTimeout(() => {
      if (!emailCaptured) {
        setShowEmailModal(true);
      }
    }, 5000);

    return () => clearTimeout(timer);
  }, [emailCaptured]);

  const trackData = {
    apostolic_prophetic: {
      name: "Apostolic-Prophetic Leadership Track",
      icon: Target,
      color: "purple",
      strengths: [
        "Prophetic sensitivity and discernment",
        "Desire to operate in spiritual authority",
        "Heart for teaching and equipping others",
        "Hunger for deeper revelation"
      ],
      outcomes: [
        "Hear God's voice with crystal clarity",
        "Minister with prophetic accuracy",
        "Lead with apostolic authority",
        "Build lasting kingdom impact"
      ]
    },
    minstrel_worship: {
      name: "Minstrel & Worship Leadership Track",
      icon: Star,
      color: "orange",
      strengths: [
        "Musical and creative gifting",
        "Heart for worship and God's presence",
        "Desire to lead others in worship",
        "Sensitivity to the Holy Spirit in worship"
      ],
      outcomes: [
        "Understand biblical worship theology",
        "Develop prophetic worship skills",
        "Grow as a worship leader",
        "Learn to steward God's presence"
      ]
    }
  };

  const track = trackData[recommendedTrack] || trackData.apostolic_prophetic;
  const TrackIcon = track.icon;

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/assessment/capture-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          session_id: sessionId,
          email: email,
          name: name
        })
      });

      if (response.ok) {
        setEmailCaptured(true);
        setShowEmailModal(false);
        alert('Check your email for your full personalized report and free resources!');
      } else {
        alert('Error capturing email. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      alert('Failed to submit. Please check your connection.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className={`bg-gradient-to-br from-${track.color}-600 to-${track.color}-700 text-white py-16`}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-white/20 rounded-full mb-6">
            <TrackIcon className="w-10 h-10" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            🎯 Your Results Are In!
          </h1>
          <p className="text-xl md:text-2xl text-white/90">
            Based on your answers, you're called to:
          </p>
          <div className="mt-6 inline-block bg-white text-gray-900 px-8 py-4 rounded-2xl shadow-lg">
            <p className="text-2xl md:text-3xl font-bold">{track.name}</p>
          </div>
        </div>
      </div>

      {/* Results Content */}
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Your Strengths */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Shield className={`w-8 h-8 text-${track.color}-600`} />
            <h2 className="text-2xl font-bold text-gray-900">Your Spiritual Strengths</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {track.strengths.map((strength, index) => (
              <div key={index} className="flex items-start space-x-3">
                <CheckCircle className={`w-5 h-5 text-${track.color}-600 flex-shrink-0 mt-1`} />
                <p className="text-gray-700">{strength}</p>
              </div>
            ))}
          </div>
        </div>

        {/* What This Track Will Do For You */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center space-x-3 mb-6">
            <Heart className={`w-8 h-8 text-${track.color}-600`} />
            <h2 className="text-2xl font-bold text-gray-900">What This 12-Month Journey Will Give You</h2>
          </div>
          <div className="space-y-4">
            {track.outcomes.map((outcome, index) => (
              <div key={index} className={`flex items-start space-x-3 p-4 bg-${track.color}-50 rounded-lg`}>
                <Star className={`w-5 h-5 text-${track.color}-600 flex-shrink-0 mt-1`} />
                <p className="text-gray-900 font-medium">{outcome}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`bg-gradient-to-br from-${track.color}-600 to-${track.color}-700 rounded-2xl p-8 text-center text-white shadow-xl`}>
          <Sparkles className="w-12 h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold mb-4">
            Ready to Begin Your Journey?
          </h2>
          <p className="text-xl mb-6 text-white/90">
            Choose your payment plan and start your 12-month transformation today.
          </p>
          <button
            onClick={() => navigate(`/pricing?track=${recommendedTrack}`)}
            className="bg-white text-gray-900 px-8 py-4 rounded-xl font-semibold text-lg hover:bg-gray-100 transition-all transform hover:scale-105 shadow-lg"
          >
            Choose Payment Plan & Enroll
          </button>
          <p className="mt-4 text-sm text-white/80">
            Pay in Full (Save £200) • Quarterly • Monthly options
          </p>
        </div>

        {/* Alternative CTA */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-4">Not ready to commit yet?</p>
          <button
            onClick={() => setShowEmailModal(true)}
            className="text-purple-600 hover:text-purple-700 font-medium underline"
          >
            Get free resources and stay in touch
          </button>
        </div>
      </div>

      {/* Email Capture Modal */}
      {showEmailModal && !emailCaptured && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-8 relative">
            <button
              onClick={() => setShowEmailModal(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>

            <div className="text-center mb-6">
              <Mail className="w-16 h-16 text-purple-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Unlock Your Full Report
              </h2>
              <p className="text-gray-600">
                Get detailed analysis + 3 FREE resources matched to your calling
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="john@example.com"
                />
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
                <p className="text-sm text-gray-700 font-medium mb-2">You'll receive:</p>
                <div className="space-y-2">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <FileText className="w-4 h-4 text-purple-600" />
                    <span>Full personalized assessment report (PDF)</span>
                  </div>
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <Video className="w-4 h-4 text-purple-600" />
                    <span>Personalized track recommendations</span>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-gradient-to-r from-purple-600 to-orange-600 text-white px-6 py-4 rounded-xl font-semibold hover:from-purple-700 hover:to-orange-700 transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Sending...' : 'Get My Full Report'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AssessmentResultsPage;
